'use client';

import classNames from '@sindresorhus/class-names';
import type {FieldError} from 'react-hook-form';

import {useLocale} from '@utils/hooks/useLocale';

import styles from './Input.module.scss';

type Props = {
  label: string;
  error?: FieldError;
  placeholder: string;
  optional?: boolean;
  textarea?: boolean;
  className?: string;
  inputName?: string;
  value: string;
  rules: Record<string, {value: number | boolean | RegExp; message: string}>;
  withLettersCounterInTextArea?: boolean;
};

export const Input = ({
  label,
  error,
  placeholder,
  optional = false,
  textarea = false,
  className,
  inputName,
  value,
  rules,
  withLettersCounterInTextArea = false,
  ...restField
}: Props) => {
  const locale = useLocale();

  const isValueLongerThanRequired =
    typeof rules.maxLength.value === 'number' &&
    value.length > rules.maxLength.value;

  return (
    <div className={classNames(styles.container, className)}>
      <p>
        {label}
        {optional && (
          <span>{locale === 'ru' ? '(необязательно)' : '(optional)'}</span>
        )}
      </p>
      <div className={classNames(styles.input, {[styles.error]: error})}>
        {textarea ? (
          <textarea
            {...restField}
            placeholder={placeholder}
            data-testid={`${inputName}_input`}
          />
        ) : (
          <input
            {...restField}
            type="text"
            placeholder={placeholder}
            data-testid={`${inputName}_input`}
          />
        )}
      </div>

      {withLettersCounterInTextArea && textarea && (
        <div
          className={classNames(styles.lettersCounter, {
            [styles.error]: isValueLongerThanRequired,
          })}
        >{`${value.length} / ${rules.maxLength?.value}`}</div>
      )}
      <span className={styles.errorMessage}>{error?.message}</span>
    </div>
  );
};
